import { LoaderFunction, Outlet } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { Roles } from '@highmobility/console-role-matrix';

import { mobxStore } from '@/store';
import { PathParams, useTypedParams } from '@/hooks/useTypedParams';
import { ROUTE_PATHS } from '@/router/routes';
import { useCurrentPath } from '@/hooks/useCurrentPath';
import { useMobx } from '@/hooks/useMobx';

import { ProjectDetails } from './ProjectDetails';
import { ProjectHeader } from './ProjectHeader';
import { ProjectNav } from './ProjectNav';

export const loader: LoaderFunction = async (args) => {
  const params = args.params as PathParams<typeof ROUTE_PATHS.PROJECT>;
  const { teams, projects, billing } = mobxStore;

  const [team, project] = await Promise.all([
    teams.fetchTeam(params.teamId),
    projects.fetchProject(params.teamId, params.projectId),
    billing.fetchCompanyProfile(params.teamId),
  ]);

  if (!team || !project) {
    throw new Response('Project or team not found', { status: 404 });
  }

  await mobxStore.marketData.fetchMarketData(project.region, project.type);
  mobxStore.marketData.setSelectedPropertiesFromOrders(project?.orders || []);

  mobxStore.ui.reset();

  return null;
};

export const ProjectTemplate = observer(() => {
  const { teamId, projectId } = useTypedParams<typeof ROUTE_PATHS.PROJECT>();
  const { projects, teams, session } = useMobx();
  const project = projects.byId[projectId]!;
  const currentPath = useCurrentPath();
  const hideSidebar = currentPath.includes(ROUTE_PATHS.VEHICLE_CLEARANCE);
  const role = session.user ? teams.getRole(teamId, session.user.id) : undefined;
  const canViewVehicleClearances = !!role && Roles.has(role, 'vehicle-clearances.view');

  return (
    <div>
      <ProjectHeader project={project} />
      <div className="fixed bottom-0 left-0 right-0 top-10 flex justify-center">
        <ProjectNav
          teamId={teamId}
          projectId={projectId}
          canViewVehicleClearances={canViewVehicleClearances}
        />
        <div className="min-w-0 flex-1 shrink">
          <Outlet />
        </div>
        {!hideSidebar && <ProjectDetails project={project} />}
      </div>
    </div>
  );
});
